/**
 * useChat Hook
 *
 * Manages AI chat state for ServiceNow records.
 *
 * History is persisted per-record in localStorage so it survives tab switches
 * (the chat tab unmounts) and record reloads. Storage is browser-local only and
 * quota-safe: if the serialized history can't be stored it is silently dropped.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { sendChatMessage, type RecordContext, type ChatMessage, type ChatResponse, type ApiError } from '@/services/api';

interface UseChatResult {
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  sendMessage: (record: RecordContext, userMessage: string) => Promise<void>;
  clearChat: () => void;
}

// Ask AI history is persisted per-record in localStorage. Keyed by sys_id so each
// record keeps its own conversation.
const chatStorageKey = (recordSysId: string) => `rezolve:askai:${recordSysId}`;

function loadStoredMessages(recordSysId: string | undefined): ChatMessage[] {
  if (!recordSysId) return [];
  try {
    const raw = localStorage.getItem(chatStorageKey(recordSysId));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveStoredMessages(recordSysId: string | undefined, messages: ChatMessage[]) {
  if (!recordSysId) return;
  const key = chatStorageKey(recordSysId);
  try {
    if (messages.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(messages));
    }
  } catch {
    // Quota exceeded or storage unavailable — drop persistence for this round;
    // the in-memory conversation is unaffected.
  }
}

/**
 * @param recordSysId  When provided, chat history is persisted to localStorage
 *                     keyed by this sys_id and restored on (re)mount.
 */
export function useChat(recordSysId?: string): UseChatResult {
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadStoredMessages(recordSysId));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the sys_id we last loaded history for, so we can swap conversations
  // when the user switches records without losing the per-record history.
  const loadedSysIdRef = useRef<string | undefined>(recordSysId);
  // Set when a record swap replaces `messages` via load; the next save is skipped
  // because at that point `messages` still holds the *previous* record's data
  // (the setMessages from the load effect hasn't been applied yet). Without this,
  // the swap would persist the old conversation under the new record's key.
  const skipNextSaveRef = useRef(false);

  // When the record changes, load that record's stored history.
  useEffect(() => {
    if (recordSysId === loadedSysIdRef.current) return;
    loadedSysIdRef.current = recordSysId;
    skipNextSaveRef.current = true;
    setMessages(loadStoredMessages(recordSysId));
    setError(null);
  }, [recordSysId]);

  // Persist history whenever it changes (best-effort; see saveStoredMessages).
  useEffect(() => {
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    saveStoredMessages(recordSysId, messages);
  }, [recordSysId, messages]);

  /**
   * Send a message and get AI response
   */
  const sendMessage = useCallback(async (record: RecordContext, userMessage: string) => {
    if (!userMessage.trim()) return;

    // Add user message to history
    const newUserMessage: ChatMessage = { role: 'user', content: userMessage.trim() };
    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);
    setLoading(true);
    setError(null);

    try {
      console.log(`[useChat] Sending message for ${record.number}`);
      const response: ChatResponse = await sendChatMessage(record, updatedMessages);

      // Check for valid response content
      const messageContent = response.message?.trim();
      if (!messageContent) {
        console.warn('[useChat] Empty response received');
        setError('Received empty response from AI. Please try again.');
        setMessages(messages); // Revert to previous state
        return;
      }

      // Add assistant response to history
      const assistantMessage: ChatMessage = { role: 'assistant', content: messageContent };
      setMessages([...updatedMessages, assistantMessage]);

      console.log('[useChat] Response received');
    } catch (err) {
      const apiError = err as ApiError;
      console.error('[useChat] Error:', apiError.message);

      if (apiError.status === 429) {
        setError('AI token limit reached. Please try again later.');
      } else if (apiError.status === 401) {
        setError('Authentication failed. Check API key configuration.');
      } else if (apiError.status === 503) {
        setError('AI service not configured. Contact administrator.');
      } else {
        setError(apiError.message || 'Failed to send message');
      }

      // Remove the user message that failed
      setMessages(messages);
    } finally {
      setLoading(false);
    }
  }, [messages]);

  /**
   * Clear chat history
   */
  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return {
    messages,
    loading,
    error,
    sendMessage,
    clearChat,
  };
}
